import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

const Section = ({ n, title, children }) => (
  <section className="mt-10">
    <h2 className="text-xl font-semibold text-white tracking-tight mb-3">
      <span className="text-[#00ff88] font-mono mr-2">{n}.</span>
      {title}
    </h2>
    <div className="text-[15px] leading-relaxed text-[#c8c8d0] space-y-3">{children}</div>
  </section>
);

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy — CheatCraft",
    description: "How CheatCraft collects, stores and protects your personal data — GDPR-compliant privacy policy for creators and companies.",
    canonicalPath: "/privacy",
  });
  return (
    <div data-testid="privacy-page" className="min-h-[80vh] px-6 py-16">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#a0a0b0] hover:text-white mb-8"
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="cc-overline">Legal</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mt-2 mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#606070] font-mono">Last updated: May 2026</p>

        <div className="mt-10">
          <Section n="1" title="Introduction">
            <p>
              CheatCraft.net (&ldquo;CheatCraft&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) is committed to protecting
              your privacy. This Privacy Policy explains how we collect, use, store, and protect your personal
              information when you use our platform.
            </p>
          </Section>

          <Section n="2" title="Information We Collect">
            <p>
              <strong className="text-white">Account Information:</strong> email address, username, password (stored
              encrypted, never in plain text), account role (Creator or Company).
            </p>
            <p>
              <strong className="text-white">Profile Information:</strong> for Creators: games, video quality, equipment
              details, languages, portfolio links, LTC wallet address. For Companies: company name, website,
              description.
            </p>
            <p>
              <strong className="text-white">Transaction Data:</strong> order details, payment amounts, payment methods,
              transaction history, invoices, wallet addresses used for withdrawals.
            </p>
            <p>
              <strong className="text-white">Communication Data:</strong> messages sent through the platform&rsquo;s
              chat system, uploaded files.
            </p>
            <p>
              <strong className="text-white">Technical Data:</strong> IP address, browser type and version, device
              information, cookies, access timestamps, referral URLs.
            </p>
            <p>
              <strong className="text-white">File Data:</strong> files uploaded through the platform, file hashes
              generated for security scanning purposes.
            </p>
          </Section>

          <Section n="3" title="How We Use Your Information">
            <p>
              To provide, maintain, and operate the CheatCraft platform. To process payments and manage escrow
              transactions. To verify your identity and prevent fraud. To scan uploaded files for security threats using
              third-party tools. To send transactional emails including order confirmations, delivery notifications, and
              payment notifications. To improve our platform and user experience. To enforce our Terms of Service. To
              comply with legal obligations, including tax reporting requirements under the EU DAC7 directive where
              applicable.
            </p>
          </Section>

          <Section n="4" title="File Scanning & Security">
            <p>
              Files uploaded to CheatCraft may be scanned using third-party security services, including VirusTotal, to
              detect potential malware, stealers, or other malicious software. When a file is scanned, the file hash (a
              digital fingerprint) may be shared with the scanning service. File contents may be analyzed by the
              security service in accordance with their own privacy policies. Scan results (detection counts, behavioral
              analysis) are stored in our database and displayed to users.
            </p>
          </Section>

          <Section n="5" title="Payment Data">
            <p>
              Credit card payments are processed by Stripe. CheatCraft does not store your full credit card number.
              Stripe handles all payment data in accordance with PCI-DSS standards. Cryptocurrency payments are
              processed via NOWPayments. Wallet addresses used for payments and withdrawals are stored in our database.
              Transaction hashes are stored for record-keeping purposes.
            </p>
          </Section>

          <Section n="6" title="Data Sharing">
            <p>We do not sell your personal information to third parties. We may share your information with:</p>
            <p>
              <strong className="text-white">Service Providers:</strong> Stripe (payments), NOWPayments (cryptocurrency),
              VirusTotal (file scanning), OuiHeberg (email delivery) — only to the extent necessary to provide our
              services.
            </p>
            <p>
              <strong className="text-white">Legal Authorities:</strong> if required by law, court order, or government
              regulation, or to protect the rights, safety, or property of CheatCraft or its users.
            </p>
            <p>
              <strong className="text-white">Other Users:</strong> your public profile information (username, games,
              ratings, portfolio) is visible to other platform users. Private information (email, wallet address,
              transaction details) is never shared with other users.
            </p>
          </Section>

          <Section n="7" title="Data Retention">
            <p>
              Account data is retained for as long as your account is active. Transaction records are retained for 5
              years after the transaction date, in compliance with financial record-keeping obligations. Chat messages
              are retained for 1 year after the last message in a conversation. If you delete your account, your
              personal data is removed within 30 days, except for data we are legally required to retain.
            </p>
          </Section>

          <Section n="8" title="Data Security">
            <p>
              All passwords are hashed using bcrypt and never stored in plain text. All connections use HTTPS/TLS
              encryption. JWT tokens are stored in HttpOnly cookies to prevent XSS attacks. We implement rate limiting
              and brute-force protection on authentication endpoints. Regular security reviews are conducted to identify
              and fix vulnerabilities.
            </p>
          </Section>

          <Section n="9" title="Cookies">
            <p>CheatCraft uses essential cookies only:</p>
            <p>
              <strong className="text-white">Authentication cookies:</strong> to keep you logged in (HttpOnly, secure
              JWT tokens).{" "}
              <strong className="text-white">Session cookies:</strong> to maintain your session while using the
              platform.
            </p>
            <p>
              We do not use advertising cookies, tracking cookies, or third-party analytics cookies. We do not track you
              across other websites.
            </p>
          </Section>

          <Section n="10" title="Your Rights (GDPR)">
            <p>If you are located in the European Union, you have the following rights:</p>
            <p>
              <strong className="text-white">Right of Access:</strong> you can request a copy of all personal data we
              hold about you. <strong className="text-white">Right to Rectification:</strong> you can request correction
              of inaccurate personal data. <strong className="text-white">Right to Erasure:</strong> you can request
              deletion of your personal data (subject to legal retention requirements).{" "}
              <strong className="text-white">Right to Data Portability:</strong> you can request your data in a
              structured, machine-readable format. <strong className="text-white">Right to Object:</strong> you can
              object to certain processing of your personal data.{" "}
              <strong className="text-white">Right to Withdraw Consent:</strong> where processing is based on consent,
              you may withdraw it at any time.
            </p>
            <p>
              To exercise any of these rights, contact us at:{" "}
              <a className="text-[#00ff88] hover:underline font-mono" href="mailto:privacy@cheatcraft.net">
                privacy@cheatcraft.net
              </a>
              . We will respond within 30 days.
            </p>
          </Section>

          <Section n="11" title="Age Requirement">
            <p>
              CheatCraft is not intended for users under 18 years of age. We do not knowingly collect personal
              information from minors. If we discover that we have collected data from a user under 18, we will delete
              their account and data promptly.
            </p>
          </Section>

          <Section n="12" title="International Data Transfers">
            <p>
              Your data is stored on servers located in the European Union. If data is transferred outside the EU (for
              example, to third-party services like VirusTotal or Stripe), appropriate safeguards are in place in
              accordance with GDPR requirements.
            </p>
          </Section>

          <Section n="13" title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Users will be notified of significant changes via
              email or platform notification. The &ldquo;Last updated&rdquo; date at the top of this page reflects the
              most recent revision.
            </p>
          </Section>

          <Section n="14" title="Contact">
            <p>For privacy-related questions or to exercise your data rights:</p>
            <p>
              Email:{" "}
              <a
                data-testid="privacy-contact-email"
                className="text-[#00ff88] hover:underline font-mono"
                href="mailto:cheatcraftsupport@gmail.com"
              >
                cheatcraftsupport@gmail.com
              </a>
              <br />
              Data Controller: CheatCraft, France.
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-6 border-t border-[#15152a] flex items-center justify-between text-xs text-[#606070]">
          <Link to="/terms" data-testid="privacy-to-terms" className="hover:text-white">
            ← Terms of Service
          </Link>
          <span className="font-mono">CHEATCRAFT · © 2026</span>
        </div>
      </div>
    </div>
  );
}
